import { useCallback, useEffect, useState } from 'react';

// Month/year grouping shared by receipt list pages — chunks a chronologically
// sorted list into collapsible month buckets with sticky headers, and persists
// which buckets are collapsed per page (via storageKey) across reloads.

export interface MonthGroup<T> {
  key: string;
  label: string;
  rows: T[];
}

function monthYearLabel(iso: string): string {
  const [y, m] = iso.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

// Assumes rows are already sorted so each month's rows are contiguous.
export function groupByMonth<T>(rows: T[], getDate: (r: T) => string | null): MonthGroup<T>[] {
  const groups: MonthGroup<T>[] = [];
  const byKey = new Map<string, MonthGroup<T>>();
  for (const r of rows) {
    const date = getDate(r);
    const key = date ? date.slice(0, 7) : 'unknown';
    let g = byKey.get(key);
    if (!g) {
      g = { key, label: date ? monthYearLabel(date) : 'No date', rows: [] };
      byKey.set(key, g);
      groups.push(g);
    }
    g.rows.push(r);
  }
  return groups;
}

function loadStoredCollapsed(storageKey: string): Set<string> | null {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return null;
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? new Set(arr) : null;
  } catch {
    return null;
  }
}

// Default to only the most recent month expanded — with hundreds of receipts,
// rendering every month open on first load is unscannable. Falls back to
// whatever the user last left collapsed, once, so their state survives reloads.
export function useGroupCollapse<T>(storageKey: string, groups: MonthGroup<T>[] | null) {
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (initialized || !groups || groups.length === 0) return;
    const stored = loadStoredCollapsed(storageKey);
    setCollapsed(stored ?? new Set(groups.slice(1).map(g => g.key)));
    setInitialized(true);
  }, [groups, initialized, storageKey]);

  useEffect(() => {
    if (!initialized) return;
    window.localStorage.setItem(storageKey, JSON.stringify(Array.from(collapsed)));
  }, [collapsed, initialized, storageKey]);

  const toggleGroup = useCallback((key: string) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setCollapsed(prev => prev.size ? new Set() : new Set((groups ?? []).map(g => g.key)));
  }, [groups]);

  return { collapsed, toggleGroup, toggleAll };
}
